/**
 * @file api.ts
 * @brief TanStack Query hooks for Company Admin → Schedule (diary).
 *
 * @author DES
 * @date 19-06-2026
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
} from "@/shared/api/client";
import { scheduleEndpoints } from "@/shared/api/endpoints";
import {
  S2SLanguagesResponseSchema,
  ScheduledSessionSchema,
  ScheduledSessionsResponseSchema,
  type ScheduleSessionInput,
  type ScheduledSession,
} from "./schemas";

export const scheduleKeys = {
  all: ["company", "schedule"] as const,
  list: () => ["company", "schedule", "list"] as const,
  languages: () => ["s2s", "languages"] as const,
};

export function useScheduledSessions() {
  return useQuery<ScheduledSession[]>({
    queryKey: scheduleKeys.list(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(scheduleEndpoints.list);
      const parsed = ScheduledSessionsResponseSchema.parse(raw);
      return parsed.sessions;
    },
  });
}

export function useS2SLanguages() {
  return useQuery({
    queryKey: scheduleKeys.languages(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(scheduleEndpoints.languages);
      const parsed = S2SLanguagesResponseSchema.parse(raw);
      return parsed.languages;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateScheduledSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ScheduleSessionInput) => {
      const raw = await apiPost<unknown>(scheduleEndpoints.list, input);
      const row = (raw as { session?: unknown }).session ?? raw;
      return ScheduledSessionSchema.parse(row);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useUpdateScheduledSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: ScheduleSessionInput;
    }) => {
      const raw = await apiPut<unknown>(scheduleEndpoints.byId(id), input);
      const row = (raw as { session?: unknown }).session ?? raw;
      return ScheduledSessionSchema.parse(row);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useCancelScheduledSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(scheduleEndpoints.byId(id));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}
